import { useState } from 'react'
import { Menu } from '../../types/menu'
import { MenuPreview } from '../menu/MenuPreview'
import { MenuEditingInterface } from '../menu/MenuEditingInterface'
import { EmbedGenerator } from '../embed/EmbedGenerator'
import { TextStyleModal } from '../styling/TextStyleModal'
import { CurrencySelect } from '../editor/CurrencySelect'
import { InfoModal } from '../editor/InfoModal'
import { ConfirmNewMenuModal } from '../editor/ConfirmNewMenuModal'
import { WarningsBanner } from '../editor/WarningsBanner'
import { ChevronIcon } from '../icons'

interface EditorScreenProps {
  menu: Menu
  onMenuChange: (menu: Menu) => void
  onStartNewMenu: () => void
  warnings: string[]
  onDismissWarnings: () => void
}

/** Preview on the left; editing tools or embed code on the right */
export const EditorScreen = ({ menu, onMenuChange, onStartNewMenu, warnings, onDismissWarnings }: EditorScreenProps) => {
  const [showEmbedCode, setShowEmbedCode] = useState(false)
  const [embedPreviewMenu, setEmbedPreviewMenu] = useState<Menu | null>(null)
  const [showTextStyle, setShowTextStyle] = useState(false)
  const [showInfo, setShowInfo] = useState(false)
  const [showNewMenuConfirm, setShowNewMenuConfirm] = useState(false)

  const toggleEmbedCode = () => {
    setShowEmbedCode((shown) => !shown)
    setEmbedPreviewMenu(null)
  }

  return (
    <div className="h-screen bg-gray-100 flex relative">
      {/* Left: live preview */}
      <div className="w-1/2 bg-gray-200 p-6 overflow-y-auto pb-16">
        <h2 className="text-lg font-medium text-gray-600 mb-4">Menu Preview</h2>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <MenuPreview menu={showEmbedCode && embedPreviewMenu ? embedPreviewMenu : menu} />
        </div>
      </div>

      {/* Right: toolbar and editor or embed code */}
      <div style={{ backgroundColor: '#FBFBFB' }} className="w-1/2 overflow-y-auto px-6 pb-16">
        <div className="border-gray-200 p-6">
          <div className="flex items-center justify-between">
            {!showEmbedCode && (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowTextStyle(true)}
                  className="px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-80 bg-brand-primary"
                >
                  Edit styles
                </button>
                <CurrencySelect
                  value={menu.currency ?? '$'}
                  onChange={(currency) => onMenuChange({ ...menu, currency })}
                />
                <button
                  onClick={() => setShowInfo(true)}
                  className="w-8 h-8 rounded-full bg-gray-300 hover:bg-gray-400 flex items-center justify-center transition-colors"
                  title="How it works"
                >
                  <span className="text-white font-medium text-sm">i</span>
                </button>
              </div>
            )}
            <div className="flex items-center space-x-3">
              {!showEmbedCode && (
                <button
                  onClick={() => setShowNewMenuConfirm(true)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  New menu
                </button>
              )}
              <button
                onClick={toggleEmbedCode}
                className={
                  showEmbedCode
                    ? 'px-4 py-2 text-sm font-medium rounded-md flex items-center space-x-2 bg-transparent border text-brand-primary hover:bg-brand-primary/10'
                    : 'px-5 py-2.5 text-sm font-semibold text-white rounded-md bg-green-600 hover:bg-green-700 transition-colors flex items-center space-x-2 shadow-sm'
                }
                style={showEmbedCode ? { borderColor: 'var(--color-brand-primary)' } : {}}
              >
                {showEmbedCode ? (
                  <>
                    <ChevronIcon direction="left" />
                    <span>Back to editing</span>
                  </>
                ) : (
                  <>
                    <span>Get menu code</span>
                    <ChevronIcon direction="right" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {!showEmbedCode && warnings.length > 0 && <WarningsBanner warnings={warnings} onDismiss={onDismissWarnings} />}

        <div className="flex-1 overflow-y-auto">
          <div className="bg-white rounded-b-lg mb-6">
            {showEmbedCode ? (
              <div className="p-6">
                <EmbedGenerator menu={menu} onChange={setEmbedPreviewMenu} />
              </div>
            ) : (
              <MenuEditingInterface menu={menu} onChange={onMenuChange} />
            )}
          </div>
        </div>
      </div>

      <TextStyleModal isOpen={showTextStyle} onClose={() => setShowTextStyle(false)} menu={menu} onChange={onMenuChange} />

      {showInfo && <InfoModal onClose={() => setShowInfo(false)} />}

      {showNewMenuConfirm && (
        <ConfirmNewMenuModal
          onCancel={() => setShowNewMenuConfirm(false)}
          onConfirm={() => {
            setShowNewMenuConfirm(false)
            onStartNewMenu()
          }}
        />
      )}
    </div>
  )
}
